import AutoTopUpSettings from './AutoTopUpSettings';
import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { StyleSheet, View, ActivityIndicator, Pressable, Linking } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { useAuthStore, useModalStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import { trackEvent } from '@/constants/utils/analytics';
import { useTranslation } from '@/hooks/useTranslation';
import { formatCreditBalance } from '@/lib/formatCredits';

const createStyles = (colors, isDark, isXS) => StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Credit cards row
  creditsRow: {
    flexDirection: isXS ? 'column' : 'row',
    gap: 12,
    marginBottom: 20,
  },
  creditCard: {
    flex: 1,
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    padding: 14,
  },
  creditCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  creditCardIconContainer: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.text_color_primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  creditCardValue: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
  },
  creditCardMeta: {
    marginTop: 6,
  },
  progressBar: {
    height: 4,
    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
    borderRadius: 2,
    marginTop: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  
  // Button container
  buttonContainer: {
    marginTop: 8,
  },
});

function CreditsSettings() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isDark, isXS), [colors, isDark, isXS]);

  const { openModal } = useModalStore(state => state.actions);
  const { t } = useTranslation();
  
  const userCredits = useAuthStore(state => state.user?.credits);
  const fetchUserCredits = useAuthStore(state => state.fetchUserCredits);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadCredits = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try { if (!await fetchUserCredits({ force: true })) setLoadError(true); }
    catch { setLoadError(true); }
    finally { setLoading(false); }
  }, [fetchUserCredits]);
  useEffect(() => { loadCredits(); }, [loadCredits]);

  useEffect(() => {
    trackEvent('conversion', 'credits_settings_viewed', isPlus ? 'plus' : 'free');
  }, []);

  const handleOpenCreditsPurchaseModal = useCallback(() => {
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal]);

  if (!userCredits && loadError) return <View style={{ gap: 16, paddingVertical: 24 }}>
    <Text accessibilityRole="alert" color="danger">{t('settings.usage.balanceFailed')}</Text>
    <Button text={t('common.retry')} onPress={loadCredits} isLoading={loading} />
  </View>;

  if (!userCredits) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.text_color_primary} />
      </View>
    );
  }

  const monthlyCredits = userCredits.monthlyAllotment;
  const purchasedCredits = userCredits.purchased;
  const hasMonthly = isPlus && monthlyCredits;
  const usagePercent = hasMonthly && monthlyCredits.total > 0 ? Math.max(0, (monthlyCredits.used / monthlyCredits.total) * 100) : 0;

  return (
    <View style={styles.container}>
      {loadError && <View style={{ gap: 12, marginBottom: 20 }}>
        <Text accessibilityRole="alert" color="danger">{t('settings.usage.balanceFailed')}</Text>
        <Button text={t('common.retry')} onPress={loadCredits} isLoading={loading} />
      </View>}
      {/* Credit Cards */}
      <View style={styles.creditsRow}>
        {/* Monthly Credits */}
        {hasMonthly && (
          <View style={styles.creditCard}>
            <View style={styles.creditCardHeader}>
              <View style={styles.creditCardIconContainer}>
                <Icon name="sparkles" size={14} color={colors.text_color_primary} solid />
              </View>
              <Text size="s" color="secondary">{t('settings.credits.monthly')}</Text>
            </View>
            <View style={styles.creditCardValue}>
              <Text size="xl" weight="bold" color="primary">
                {formatCreditBalance(monthlyCredits.remaining)}
              </Text>
              <Text size="s" color="tertiary">/ {formatCreditBalance(monthlyCredits.total)}</Text>
            </View>
            <View style={styles.progressBar}>
              <View style={[styles.progressFill, { 
                width: `${Math.min(usagePercent, 100)}%`,
                backgroundColor: usagePercent < 50 ? colors.text_color_primary : usagePercent < 80 ? '#F59E0B' : colors.color_destructive
              }]} />
            </View>
            <Text size="xs" color="tertiary" style={styles.creditCardMeta}>
              {t('settings.credits.resets')} {new Date(monthlyCredits.resetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Text>
          </View>
        )}

        {/* Purchased Credits */}
        <View style={styles.creditCard}>
          <View style={styles.creditCardHeader}>
            <View style={styles.creditCardIconContainer}>
              <Icon name="coins" size={14} color={colors.text_color_primary} solid />
            </View>
            <Text size="s" color="secondary">{t('settings.credits.purchased')}</Text>
          </View>
          <View style={styles.creditCardValue}>
            <Text size="xl" weight="bold" color="primary">
              {formatCreditBalance(purchasedCredits?.available)}
            </Text>
          </View>
          <Text size="xs" color="tertiary" style={styles.creditCardMeta}>
            {t('settings.credits.neverExpires')}
          </Text>
        </View>
      </View>

      <AutoTopUpSettings />

      {/* Buy Credits Button */}
      <View style={styles.buttonContainer}>
        <Button
          variant="primary"
          text={t('settings.credits.buyCredits')}
          onPress={handleOpenCreditsPurchaseModal}
          isFullWidth
          prependIcon="coins"
        />
      </View>
    </View>
  );
}

export default React.memo(CreditsSettings);
